import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import MediaManagerModal from 'src/common/components/modals/MediaManagerModal';
import useDeferredFileUsage from 'src/hooks/useDeferredFileUsage';
import { UploadedFile } from 'src/interfaces/FileManager';
import { IHeroSettings } from 'src/interfaces/HeroSettings';

const BG_ALIGN_H = ['left', 'center', 'right'] as const;
const BG_ALIGN_V = ['top', 'center', 'bottom'] as const;

interface HeroBackgroundPanelProps {
  heroSettings: IHeroSettings | undefined;
  onChange: (settings: IHeroSettings) => void;
  contentKey: string;
  pageContentId?: string;
  contentType?: string;
  fieldName?: string;
}

export default function HeroBackgroundPanel({
  heroSettings,
  onChange,
  contentKey,
  pageContentId,
  contentType = 'pageContent',
  fieldName = 'hero-background',
}: HeroBackgroundPanelProps) {
  const { t } = useTranslation();
  const [backgroundMediaOpen, setBackgroundMediaOpen] = useState(false);

  const { addPendingFile } = useDeferredFileUsage({
    contentType,
    contentId: pageContentId || contentKey,
    fieldName,
  });

  const effective = heroSettings ?? {
    backgroundAlignH: 'center' as const,
    backgroundAlignV: 'center' as const,
  };

  const handleBackgroundSelect = (file: UploadedFile) => {
    if (file.uploadEndpoint !== 'image') return;
    addPendingFile(file.id);
    onChange({
      ...effective,
      backgroundImageUrl: file.fileUrl,
      backgroundImageFileId: file.id,
    });
  };

  const handleBackgroundClear = () => {
    onChange({
      ...effective,
      backgroundImageUrl: undefined,
      backgroundImageFileId: undefined,
    });
  };

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: { sm: '3 1 0' }, width: { xs: '100%' }, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('system.pages.hero.backgroundImage')}
          </Typography>
          {effective.backgroundImageUrl ? (
            <CardMedia
              component="img"
              src={effective.backgroundImageUrl}
              sx={{
                width: '100%',
                maxHeight: 160,
                objectFit: 'cover',
                borderRadius: 1,
                mb: 1,
              }}
            />
          ) : null}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => setBackgroundMediaOpen(true)}>
              {t('system.pages.hero.selectBackgroundImage')}
            </Button>
            {effective.backgroundImageUrl ? (
              <Button variant="text" size="small" color="secondary" onClick={handleBackgroundClear}>
                {t('system.pages.hero.clearBackgroundImage')}
              </Button>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ flex: { sm: '1 1 0' }, width: { xs: '100%' }, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            {t('system.pages.hero.backgroundPosition')}
          </Typography>
          <Box
            role="group"
            aria-label={t('system.pages.hero.backgroundPosition')}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 0.75,
              width: '100%',
            }}
          >
            {BG_ALIGN_V.flatMap(v =>
              BG_ALIGN_H.map(h => {
                const isSelected =
                  (effective.backgroundAlignH ?? 'center') === h &&
                  (effective.backgroundAlignV ?? 'center') === v;
                return (
                  <Button
                    key={`${v}-${h}`}
                    size="small"
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() =>
                      onChange({
                        ...effective,
                        backgroundAlignH: h,
                        backgroundAlignV: v,
                      })
                    }
                    sx={{ minWidth: 0, px: 0, py: 1 }}
                    aria-label={`${t(`system.pages.hero.align.${v === 'center' ? 'middle' : v}`)} / ${t(`system.pages.hero.align.${h}`)}`}
                  >
                    {v === 'top' ? '↑' : v === 'center' ? '•' : '↓'}
                    {h === 'left' ? '←' : h === 'center' ? '•' : '→'}
                  </Button>
                );
              }),
            )}
          </Box>
        </Box>
      </Box>
      <MediaManagerModal
        open={backgroundMediaOpen}
        onClose={() => setBackgroundMediaOpen(false)}
        onSelect={handleBackgroundSelect}
        mode="select"
        endpoint="image"
        title={t('system.pages.hero.selectBackgroundImage')}
        showUploadButton
      />
    </>
  );
}
